import { useEffect, useRef } from 'react';
import type { Coordenada, PuntoViaje } from '../../lib/appTypes';

interface Props {
  coordenadas: Coordenada[];
  coordenadaActual?: Coordenada;
  coordenadaInicio?: Coordenada;
  coordenadaFin?: Coordenada;
  puntosIntermedios?: PuntoViaje[];
  conductorNombre?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LeafletLib = any;
type WinWithL = Window & { L?: LeafletLib };

// Cargamos Leaflet dinámicamente para no necesitar npm install
function loadLeaflet(): Promise<LeafletLib> {
  return new Promise((resolve, reject) => {
    const win = window as unknown as WinWithL;
    if (win.L) { resolve(win.L); return; }
    // CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    // JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve((window as unknown as WinWithL).L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function MapaAdmin({
  coordenadas,
  coordenadaActual,
  coordenadaInicio,
  coordenadaFin,
  puntosIntermedios = [],
  conductorNombre,
}: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstanceRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerRef = useRef<any>(null);
  const startMarkerRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const polylineRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const destinationMarkerRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const waypointMarkersRef = useRef<any[]>([]);

  useEffect(() => {
    let mounted = true;
    loadLeaflet().then(L => {
      if (!mounted || !mapRef.current) return;
      if (mapInstanceRef.current) return; // ya inicializado

      const defaultCoord: [number, number] = coordenadaActual
        ? [coordenadaActual.lat, coordenadaActual.lng]
        : coordenadaInicio
        ? [coordenadaInicio.lat, coordenadaInicio.lng]
        : coordenadaFin
        ? [coordenadaFin.lat, coordenadaFin.lng]
        : puntosIntermedios[0]?.latitud != null && puntosIntermedios[0]?.longitud != null
        ? [puntosIntermedios[0].latitud, puntosIntermedios[0].longitud]
        : [-0.2298500, -78.5249500]; // Quito por defecto

      const map = L.map(mapRef.current).setView(defaultCoord, 13);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      // Icono personalizado para el conductor
      const icon = L.divIcon({
        html: '<div style="font-size:24px;line-height:1">🚗</div>',
        className: '',
        iconAnchor: [12, 12],
      });

      if (coordenadaActual) {
        markerRef.current = L.marker(
          [coordenadaActual.lat, coordenadaActual.lng],
          { icon },
        )
          .addTo(map)
          .bindPopup(conductorNombre ?? 'Conductor')
          .openPopup();
      }

      if (coordenadas.length > 1) {
        const latlngs = coordenadas.map(c => [c.lat, c.lng] as [number, number]);
        polylineRef.current = L.polyline(latlngs, { color: '#2563eb', weight: 3 }).addTo(map);
      }
    });

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
        startMarkerRef.current = null;
        polylineRef.current = null;
        destinationMarkerRef.current = null;
        waypointMarkersRef.current = [];
      }
    };
  }, []); // solo montar una vez

  // Actualizar marcador y ruta cuando cambian coordenadas
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    loadLeaflet().then(L => {
      const startIcon = L.divIcon({
        html: '<div style="font-size:24px;line-height:1">🟢</div>',
        className: '',
        iconAnchor: [12, 24],
      });

      if (coordenadaInicio) {
        if (startMarkerRef.current) {
          startMarkerRef.current.setLatLng([coordenadaInicio.lat, coordenadaInicio.lng]);
        } else {
          startMarkerRef.current = L.marker([coordenadaInicio.lat, coordenadaInicio.lng], { icon: startIcon })
            .addTo(map)
            .bindPopup('Punto de partida');
        }
      } else if (startMarkerRef.current) {
        map.removeLayer(startMarkerRef.current);
        startMarkerRef.current = null;
      }

      if (coordenadaActual) {
        if (markerRef.current) {
          markerRef.current.setLatLng([coordenadaActual.lat, coordenadaActual.lng]);
          markerRef.current.setPopupContent(conductorNombre ?? 'Conductor');
        } else {
          const icon = L.divIcon({
            html: '<div style="font-size:24px;line-height:1">🚗</div>',
            className: '',
            iconAnchor: [12, 12],
          });
          markerRef.current = L.marker(
            [coordenadaActual.lat, coordenadaActual.lng],
            { icon },
          )
            .addTo(map)
            .bindPopup(conductorNombre ?? 'Conductor')
            .openPopup();
        }
        map.panTo([coordenadaActual.lat, coordenadaActual.lng]);
      } else if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }

      if (coordenadas.length > 1) {
        const latlngs = coordenadas.map(c => [c.lat, c.lng] as [number, number]);
        if (polylineRef.current) {
          polylineRef.current.setLatLngs(latlngs);
        } else {
          polylineRef.current = L.polyline(latlngs, { color: '#2563eb', weight: 3 }).addTo(map);
        }
      } else if (polylineRef.current) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }

      const destinationIcon = L.divIcon({
        html: '<div style="font-size:24px;line-height:1">🎯</div>',
        className: '',
        iconAnchor: [12, 24],
      });

      if (coordenadaFin) {
        if (destinationMarkerRef.current) {
          destinationMarkerRef.current.setLatLng([coordenadaFin.lat, coordenadaFin.lng]);
        } else {
          destinationMarkerRef.current = L.marker([coordenadaFin.lat, coordenadaFin.lng], { icon: destinationIcon })
            .addTo(map)
            .bindPopup('Destino final');
        }
      } else if (destinationMarkerRef.current) {
        map.removeLayer(destinationMarkerRef.current);
        destinationMarkerRef.current = null;
      }

      waypointMarkersRef.current.forEach(marker => map.removeLayer(marker));
      waypointMarkersRef.current = [];

      const configuredPoints = puntosIntermedios
        .filter(point => typeof point.latitud === 'number' && typeof point.longitud === 'number')
        .map(point => ({ lat: point.latitud as number, lng: point.longitud as number, urlDestino: point.urlDestino }));

      configuredPoints.forEach((point, index) => {
        const waypointIcon = L.divIcon({
          html: `<div style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:9999px;background:#f97316;color:white;font-size:12px;font-weight:700;">${index + 1}</div>`,
          className: '',
          iconAnchor: [12, 12],
        });

        const marker = L.marker([point.lat, point.lng], { icon: waypointIcon })
          .addTo(map)
          .bindPopup(`Punto intermedio ${index + 1}`);
        waypointMarkersRef.current.push(marker);
      });

      const visiblePoints = [
        ...coordenadas.map(c => [c.lat, c.lng] as [number, number]),
        ...(coordenadaInicio ? [[coordenadaInicio.lat, coordenadaInicio.lng] as [number, number]] : []),
        ...(coordenadaActual ? [[coordenadaActual.lat, coordenadaActual.lng] as [number, number]] : []),
        ...configuredPoints.map(point => [point.lat, point.lng] as [number, number]),
        ...(coordenadaFin ? [[coordenadaFin.lat, coordenadaFin.lng] as [number, number]] : []),
      ];

      if (visiblePoints.length > 1) {
        map.fitBounds(visiblePoints, { padding: [28, 28], maxZoom: 15 });
      } else if (visiblePoints.length === 1) {
        const [point] = visiblePoints;
        if (point) {
          map.setView(point, 15);
        }
      }
    });
  }, [coordenadaActual, coordenadaInicio, coordenadaFin, conductorNombre, coordenadas, puntosIntermedios]);

  return (
    <div
      ref={mapRef}
      className="w-full rounded-xl overflow-hidden border border-gray-200"
      style={{ height: '400px' }}
    />
  );
}
